import { ConnectionType, Contact } from "protico-sdk";
import useSWR from "swr";
import { useAppSelector } from ".";

import { useCallback } from "react";
import { checkAddress } from "../utils/format.utils";
import { getStableBasicProfile } from "./useBasicProfile";
import useSocialConnection from "./useSocialConnection";

export interface ContactPlus extends Contact {
  name: string;
  connectionState: ConnectionState;
}

export enum ConnectionState {
  /**
   * user and target have no connection
   */
  NONE,

  /**
   * user and target are friend
   */
  FRIEND,

  /**
   * user are waiting for target's connection
   */
  WAITING,

  /**
   * user has blocked target
   */
  BLOCK,

  /**
   * user has recieved friend request from target
   */
  REQUESTED,
}

/**
 * Check the socialConnection state according to the `targetAddress`.
 * Whenever `userContactList` in the store change, it'll auto update the socialConnection state
 * based on the new `userContactList`.
 * The return `ConnectionState` might be `undefined` when it is fetching.
 * @category hook
 */
export const useConnectionCheck = (targetAddress?: string) => {
  const { currentAddress } = useAppSelector((state) => state.auth);
  const {
    contacts,
    isFetching: isUserFetching,
    getSocialConnection,
  } = useSocialConnection();

  const getConnectionVerification = async (
    targetAddress: string | undefined,
    userContacts: Contact[],
    userAddress: string
  ) => {
    try {
      checkAddress(targetAddress);
    } catch (_) {
      return { connectionState: ConnectionState.NONE, name: "user" };
    }
    const sourceFindResult = userContacts.find(
      (c) => c.target === targetAddress
    );
    if (!sourceFindResult?.connectionType)
      return {
        connectionState: ConnectionState.REQUESTED,
        name: targetAddress,
      };
    const targetProfile = await getStableBasicProfile(targetAddress);
    const targetName =
      sourceFindResult?.alias || targetProfile?.name || targetAddress;

    if (sourceFindResult?.connectionType === ConnectionType.BLOCK)
      return { connectionState: ConnectionState.BLOCK, name: targetName };

    const targetContacts = await getSocialConnection(targetAddress);
    const targetFindResult = targetContacts.find(
      (c) => c.target.toLowerCase() === userAddress
    )?.connectionType;
    if (targetFindResult === ConnectionType.CONNECT)
      return { connectionState: ConnectionState.FRIEND, name: targetName };
    return { connectionState: ConnectionState.WAITING, name: targetName };
  };

  const { data, error } = useSWR(
    [targetAddress, contacts, currentAddress],
    getConnectionVerification,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const getConnectionVerificationBatch = useCallback(async () => {
    if (contacts.length === 0) return [];
    return await Promise.all(
      contacts.map(async (contact) => {
        const connectionCheck = await getConnectionVerification(
          contact.target,
          contacts,
          currentAddress
        );
        return {
          ...contact,
          name: connectionCheck.name,
          connectionState: connectionCheck.connectionState,
        };
      })
    );
  }, [contacts]);

  return {
    isConnectionStateFetching: (!error && !data) || isUserFetching,
    connectionCheck: data ?? {
      connectionState: ConnectionState.NONE,
      name: "user",
    },
    getConnectionVerificationBatch,
  };
};
